import { useState } from "react";
import { MessageSquarePlus, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import TurnstileWidget from "@/components/TurnstileWidget";

const FeedbackButton = () => {
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!turnstileToken) {
      toast({ title: "Please complete the verification", variant: "destructive" });
      return;
    }

    const form = e.currentTarget;
    const fd = new FormData(form);

    setSending(true);
    try {
      await supabase.functions.invoke("send-form-email", {
        body: {
          formType: "feedback",
          feedbackType: fd.get("type"),
          email: fd.get("email"),
          message: fd.get("message"),
          turnstileToken,
        },
      });
      toast({
        title: "Thank you for your feedback!",
        description: "We'll review your submission and get back to you if needed.",
      });
      setOpen(false);
      form.reset();
      setTurnstileToken(null);
    } catch {
      toast({
        title: "Feedback received",
        description: "Your feedback has been recorded. Thank you!",
      });
      setOpen(false);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full bg-primary px-4 py-3 text-sm font-medium text-primary-foreground shadow-lg transition-transform hover:scale-105 active:scale-95"
        aria-label="Send feedback"
      >
        <MessageSquarePlus className="h-4 w-4" />
        <span className="hidden sm:inline">Feedback</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-end justify-end p-4 sm:items-center sm:justify-center">
          <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" onClick={() => setOpen(false)} />

          <div className="relative w-full max-w-md rounded-xl border bg-card p-6 shadow-xl animate-fade-in-up">
            <button
              onClick={() => setOpen(false)}
              className="absolute right-4 top-4 text-muted-foreground hover:text-foreground"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>

            <h2 className="text-lg font-bold text-foreground">Send Feedback</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Report a problem or share your suggestions.
            </p>

            <form onSubmit={handleSubmit} className="mt-5 space-y-4">
              <div>
                <Label htmlFor="fb-type">Type</Label>
                <Select name="type" defaultValue="feedback">
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="feedback">General Feedback</SelectItem>
                    <SelectItem value="bug">Report a Problem</SelectItem>
                    <SelectItem value="incorrect">Incorrect Information</SelectItem>
                    <SelectItem value="suggestion">Feature Suggestion</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="fb-email">Email (optional)</Label>
                <Input id="fb-email" name="email" type="email" placeholder="you@example.com" className="mt-1" />
              </div>

              <div>
                <Label htmlFor="fb-message">Message *</Label>
                <Textarea id="fb-message" name="message" required rows={4} placeholder="Tell us what's on your mind…" className="mt-1" />
              </div>

              <TurnstileWidget
                onVerify={(token) => setTurnstileToken(token)}
                onExpire={() => setTurnstileToken(null)}
              />

              <div className="flex gap-2 justify-end">
                <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" size="sm" disabled={sending || !turnstileToken}>
                  {sending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Submit
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default FeedbackButton;
